// client/src/lib/hooks/useContent.ts
import { useEffect, useState } from "react";
import { apiGet } from "../api";

export type About = { title: string; description: string; image?: string };
export type ServiceItem = { id: string; title?: string; description?: string; image_url?: string; icon?: string; position?: number; is_featured?: boolean };
export type PricingPlan = { id: string; name: string; price_display: string; summary?: string; features?: any; position?: number };
export type FaqItem = { id: string; question: string; answer: string; position: number };
export type HeroSlide = { id: string; position: number; tagline: string; image_url: string; is_active: boolean };
export type Contact = { title: string; email: string; phone?: string; copy?: string; subject_placeholder?: string; message_placeholder?: string };

type LandingData = {
  about?: About;
  services?: ServiceItem[];
  pricing?: PricingPlan[];
  faq?: FaqItem[];
  heroes?: HeroSlide[];
  contact?: Contact;
};

export function useLandingContent() {
  const [data, setData] = useState<LandingData>({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErrors({});
      const results = await Promise.allSettled([
        apiGet<About>("/api/content/about"),
        apiGet<ServiceItem[]>("/api/content/services"),
        apiGet<PricingPlan[]>("/api/content/pricing"),
        apiGet<FaqItem[]>("/api/content/faq"),
        apiGet<HeroSlide[]>("/api/content/heroes"),
        apiGet<Contact>("/api/content/contact"),
      ]);

      if (cancelled) return;

      const [aboutR, servicesR, pricingR, faqR, heroesR, contactR] = results;
      const out: LandingData = {};
      const errs: Record<string, string> = {};

      if (aboutR.status === "fulfilled") out.about = aboutR.value;
      else errs.about = aboutR.reason?.message ?? "about failed";

      if (servicesR.status === "fulfilled") out.services = servicesR.value;
      else errs.services = "unavailable";

      if (pricingR.status === "fulfilled") out.pricing = pricingR.value;
      else errs.pricing = "unavailable";

      if (faqR.status === "fulfilled") out.faq = faqR.value;
      else errs.faq = "unavailable";

      if (heroesR.status === "fulfilled") out.heroes = heroesR.value;
      else errs.heroes = "unavailable";

      if (contactR.status === "fulfilled") out.contact = contactR.value;
      else errs.contact = "unavailable";

      setData(out);
      setErrors(errs);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Only treat About failure as a "blocking" error for the About section UI
  const error = errors.about ? new Error(errors.about) : null;

  return { data, loading, error, errors };
}
